import { Injectable } from '@angular/core';
import { ApiService, HttpMethod } from '../api/api.service';
import { GetFilesApiResponse, GetFilesForUserApiResponse } from '../../models/api.model';

export interface DocumentQueryParams {
  page: number;
  pageSize: number;
  search: string;
  fileType: number[];
  extension: string[];
}

export interface DocumentDetail {
  generatedFileName?: string;
  originalFileName?: string;
  extension: string;
  uploadedAt?: string;
  typeId?: number;
  categoryId?: number;
  owner?: string;
  detail?: string;
  issuer?: string;
  documentDate?: string | null;
  taxYear?: string | null;
  fileId: string;
}

export interface DocumentByIdResponse {
  message: string;
  file: DocumentDetail;
}

export type RenameComponentsInput = {
  categoryId?: number;
  typeId?: number;
  owner?: string;
  issuer?: string;
  detail?: string;
  taxYear?: string | number | null;
  documentDate?: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  private readonly baseUrl = 'documents'; // Base route for the documents API

  constructor(private apiService: ApiService) {}

  /**
   * Test endpoint to verify API connectivity.
   * @returns Promise<string>
   */
  async testEndpoint(): Promise<string> {
    return this.apiService.call<string>(HttpMethod.GET, `${this.baseUrl}/test`);
  }

  /**
   * Upload a file to the server.
   * @param file The file to upload.
   * @returns Promise<any>
   */
  async uploadFile(file: File, metadata?: { isMultipart?: boolean; household_id?: string; }): Promise<any> {
    const formData = new FormData();

    formData.append('file', file);

    if (metadata?.isMultipart) {
      formData.append('isMultipart', 'true');
    }
    if (metadata?.household_id) {
      formData.append('household_id', metadata.household_id);
    }
    // Use the ApiService's uploadFile method
    return this.apiService.uploadFile<any>(`${this.baseUrl}/upload`, formData);
  }

  /**
   * Retrieve the list of files for the authenticated user with optional filters
   * @param params Optional query parameters for searching and filtering
   * @returns Promise<GetFilesApiResponse>
   */
  async listFiles(params: Partial<DocumentQueryParams> = {}): Promise<GetFilesApiResponse> {
    // Convert params object to URL search params
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Special handling for fileId array
          if (key === 'fileType' && Array.isArray(value)) {
            queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    const queryString = queryParams.toString();
    const url = `${this.baseUrl}/list${queryString ? `?${queryString}` : ''}`;
    return this.apiService.call<GetFilesApiResponse>(HttpMethod.GET, url);
  }
  
  /**
   * Retrieve the list of files for a specific user with optional filters
   * @param userId The user's ID
   * @param params Optional query parameters for searching and filtering
   * @returns Promise<GetFilesApiResponse>
   */
  async listFilesByUser(userId: number, params?: Partial<DocumentQueryParams>): Promise<GetFilesForUserApiResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'fileType' && Array.isArray(value)) {
            queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const queryString = queryParams.toString();
    const url = `${this.baseUrl}/user/${userId}${queryString ? `?${queryString}` : ''}`;

    return this.apiService.call<GetFilesForUserApiResponse>(HttpMethod.GET, url);
  }

  async listFilesByHousehold(householdId: string, params?: Partial<DocumentQueryParams>): Promise<{ documents: DocumentDetail[], total: number }> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'fileType' && Array.isArray(value)) {
            queryParams.append(key, value.join(','));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const queryString = queryParams.toString();
    const url = `${this.baseUrl}/household/${householdId}${queryString ? `?${queryString}` : ''}`;

    return this.apiService.call<{ documents: DocumentDetail[]; total: number }>(HttpMethod.GET, url);
  }

  /**
   * Delete a file by its key.
   * @param key The key of the file to delete.
   * @returns Promise<any>
   */
  async deleteFile(key: string): Promise<any> {
    return this.apiService.call<any>(HttpMethod.DELETE, `${this.baseUrl}/${key}`);
  }

  async renameFileByComponents(fileId: string, components: RenameComponentsInput): Promise<any> {
    const body = { fileId: fileId, ...components };
    console.log("body", body);
    return this.apiService.call<any>(HttpMethod.PUT, `${this.baseUrl}/rename/by-components`, body);
  }

  async getDocumentAccess(fileId: string, mode?: 'download' | 'view'): Promise<any> {
    const url = mode 
      ? `${this.baseUrl}/access/${fileId}?mode=${mode}`
      : `${this.baseUrl}/access/${fileId}`;
    return this.apiService.call<any>(HttpMethod.GET, url);
  }

  async downloadFile(fileId: string): Promise<any> {
    return this.getDocumentAccess(fileId, 'download');
  }

  async viewFile(fileId: string): Promise<any> {
    return this.getDocumentAccess(fileId, 'view');
  }

  async getById(fileId: string): Promise<DocumentByIdResponse> {
    return this.apiService.call<DocumentByIdResponse>(HttpMethod.GET, `${this.baseUrl}/${fileId}`);
  }
}
